/**
 * Types and functions for working with meshes in both 2d and 3d
 * @module
 */
import { aabb3AddPoint } from "./aabb3";
import type { AABB3 } from "./aabb3Types";
import { aabb3New } from "./aabb3Types";
import type { Face } from "./brep";
import { faceByThreePoints } from "./brep";
import type { Mat4 } from "./mat4";

export type IndexArray = Uint32Array;
export const IndexArray = Uint32Array;

export function indexArrayNew(size: number): IndexArray {
  return new IndexArray(size);
}

export interface Mesh2 {
  verts: Float32Array;
  tris: IndexArray;

  // In case the length of the array is 1, then the same id is used for all vertices in the mesh.
  ids?: Uint32Array;
}

export interface Mesh3 {
  verts: Float32Array;
  tris: IndexArray;
  norms?: Float32Array;
  uvs1?: Float32Array;
  uvs2?: Float32Array;

  // In case the length of the array is 1, then the same id is used for all vertices in the mesh.
  ids?: Uint32Array;
}

export interface QuadMesh3 extends Mesh3 {
  quads: IndexArray;
}

export interface Mesh3Instances {
  mesh: Mesh3;
  matrices: Mat4[];
}

export interface Mesh3AndId {
  readonly mesh: Mesh3;
  readonly id?: string;
}

export interface Mesh3IdAndName extends Mesh3AndId {
  readonly name: string;
  readonly useMeshUv: boolean;
}

export function mesh2New(verts: Float32Array, tris: Uint32Array, ids?: Uint32Array): Mesh2 {
  return { verts, tris, ids };
}

export function mesh2Copy(mesh: Mesh2): Mesh2 {
  return {
    verts: mesh.verts.slice(),
    tris: mesh.tris.slice(),
    ids: mesh.ids?.slice(),
  };
}

export function mesh3New(
  verts: Float32Array,
  tris: IndexArray,
  norms?: Float32Array,
  uvs1?: Float32Array,
  uvs2?: Float32Array,
  ids?: Uint32Array,
): Mesh3 {
  return { verts, tris, norms, uvs1, uvs2, ids };
}

export function mesh3Copy(mesh: Mesh3): Mesh3 {
  return {
    verts: mesh.verts.slice(),
    tris: mesh.tris.slice(),
    norms: mesh.norms?.slice(),
    uvs1: mesh.uvs1?.slice(),
    uvs2: mesh.uvs2?.slice(),
    ids: mesh.ids?.slice(),
  };
}

/**
 * Concatenate several meshes into a single mesh without moving or welding any
 * vertices: vertex data is appended and triangle indices are offset by the
 * running vertex count. Optional attributes (`norms`, `uvs1`, `uvs2`, `ids`)
 * are carried over only when *every* input mesh provides them with a consistent
 * per-vertex length, so the combined mesh never has mismatched attribute arrays.
 * Inputs are not mutated.
 *
 * Callers are responsible for only combining meshes that belong together (same
 * material and transform space) — this is a pure geometry join, not a weld.
 *
 * An empty input yields an empty mesh (no optional attributes), and a single
 * input is returned as-is.
 */
export function combineMesh3(meshes: Mesh3[]): Mesh3 {
  if (meshes.length === 0) {
    return mesh3New(new Float32Array(0), new IndexArray(0));
  }
  if (meshes.length === 1) {
    return meshes[0];
  }

  let vertFloatCount = 0;
  let indexCount = 0;
  let hasNorms = true;
  let hasUvs1 = true;
  let hasUvs2 = true;
  let hasIds = true;
  let allIdsAreSameSingleId = true;
  let sharedSingleId: number | undefined;
  for (const mesh of meshes) {
    const vertexCount = mesh.verts.length / 3;
    vertFloatCount += mesh.verts.length;
    indexCount += mesh.tris.length;
    hasNorms = hasNorms && mesh.norms !== undefined && mesh.norms.length === mesh.verts.length;
    hasUvs1 = hasUvs1 && mesh.uvs1 !== undefined && mesh.uvs1.length === vertexCount * 2;
    hasUvs2 = hasUvs2 && mesh.uvs2 !== undefined && mesh.uvs2.length === vertexCount * 2;
    hasIds = hasIds && mesh.ids !== undefined;

    if (mesh.ids === undefined || mesh.ids.length !== 1) {
      allIdsAreSameSingleId = false;
    } else if (sharedSingleId === undefined) {
      sharedSingleId = mesh.ids[0];
    } else if (mesh.ids[0] !== sharedSingleId) {
      allIdsAreSameSingleId = false;
    }
  }

  const vertexTotal = vertFloatCount / 3;
  const verts = new Float32Array(vertFloatCount);
  const tris = new IndexArray(indexCount);
  const norms = hasNorms ? new Float32Array(vertFloatCount) : undefined;
  const uvs1 = hasUvs1 ? new Float32Array(vertexTotal * 2) : undefined;
  const uvs2 = hasUvs2 ? new Float32Array(vertexTotal * 2) : undefined;
  let ids: Uint32Array | undefined;
  if (hasIds) {
    if (allIdsAreSameSingleId && sharedSingleId !== undefined) {
      ids = new Uint32Array([sharedSingleId]);
    } else {
      ids = new Uint32Array(vertexTotal);
    }
  }

  let vertFloatCursor = 0;
  let indexCursor = 0;
  let vertexCursor = 0;
  for (const mesh of meshes) {
    const vertexOffset = vertFloatCursor / 3;
    const vertexCount = mesh.verts.length / 3;

    verts.set(mesh.verts, vertFloatCursor);
    if (norms && mesh.norms) {
      norms.set(mesh.norms, vertFloatCursor);
    }
    if (uvs1 && mesh.uvs1) {
      uvs1.set(mesh.uvs1, vertexOffset * 2);
    }
    if (uvs2 && mesh.uvs2) {
      uvs2.set(mesh.uvs2, vertexOffset * 2);
    }
    if (ids && ids.length !== 1 && mesh.ids) {
      // A length-1 `ids` array is the shorthand for "same id for every vertex".
      if (mesh.ids.length === vertexCount) {
        ids.set(mesh.ids, vertexCursor);
      } else {
        ids.fill(mesh.ids[0], vertexCursor, vertexCursor + vertexCount);
      }
    }

    for (let i = 0, l = mesh.tris.length; i < l; i++) {
      tris[indexCursor + i] = mesh.tris[i] + vertexOffset;
    }

    vertFloatCursor += mesh.verts.length;
    indexCursor += mesh.tris.length;
    vertexCursor += vertexCount;
  }

  return mesh3New(verts, tris, norms, uvs1, uvs2, ids);
}

export function quadMesh3New(
  verts: Float32Array,
  tris: IndexArray,
  quads: IndexArray,
  norms?: Float32Array,
  uvs1?: Float32Array,
  uvs2?: Float32Array,
  ids?: Uint32Array,
): QuadMesh3 {
  return { verts, tris, quads, norms, uvs1, uvs2, ids };
}

export function getFacesFromMesh(mesh: Mesh3): Face[] {
  const faces: Face[] = [];

  const { verts, tris } = mesh;

  for (let i = 0, l = tris.length; i < l; i += 3) {
    const ind0 = mesh.tris[i];
    const ind1 = mesh.tris[i + 1];
    const ind2 = mesh.tris[i + 2];

    const face = faceByThreePoints(
      verts[ind0 * 3],
      verts[ind0 * 3 + 1],
      verts[ind0 * 3 + 2],
      verts[ind1 * 3],
      verts[ind1 * 3 + 1],
      verts[ind1 * 3 + 2],
      verts[ind2 * 3],
      verts[ind2 * 3 + 1],
      verts[ind2 * 3 + 2],
    );

    // If the triangle is degenerate, face will be null.
    if (face !== null) {
      faces.push(face);
    }
  }

  return faces;
}

export function vertexArrayAABB3(vertices: Float32Array | ArrayLike<number>): AABB3 | null {
  const l = vertices.length;
  if (l < 3) {
    return null;
  }
  const aabb = aabb3New(vertices[0], vertices[1], vertices[2], vertices[0], vertices[1], vertices[2]);
  for (let i = 3; i + 2 < l; i += 3) {
    aabb3AddPoint(aabb, vertices[i], vertices[i + 1], vertices[i + 2]);
  }
  return aabb;
}
